import "./CacheTable.scss";

import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import type { CellProps, Column } from "react-table";

import { ActionCell } from "../../../../components/ActionCell/ActionCell";
import { Checkbox } from "../../../../components/Checkbox/Checkbox";
import { DateColumn } from "../../../../components/CustomizedTable/Columns";
import { PaginatedTable } from "../../../../components/CustomizedTable/PaginatedTable";
import { SortableHeader } from "../../../../components/CustomizedTable/SortableHeader";
import { Sorting } from "../../../../components/CustomizedTable/Sorting/Sorting";
import { DeleteCacheItemPopup } from "../../../../components/DeleteCacheItemPopup/DeleteCacheItemPopup";
import { TableCellSm, TableLineSm } from "../../../../components/TableLineSm";
import { useLocationContext } from "../../../../contexts/locationContext";
import { useMediaQueryContext } from "../../../../contexts/mediaQueryProvider/MediaQueryProvider";
import { useOrganizationContext } from "../../../../contexts/organizations/organizationsContext";
import type { IApplication } from "../../../../hooks/reactQueryHooks/useApplications";
import {
  useDeleteCacheItem,
  useDeleteCacheItems,
} from "../../../../hooks/reactQueryHooks/useCacheItems";
import type { Direction } from "../../../../hooks/useSortableTable";
import { useSortableTable } from "../../../../hooks/useSortableTable";
import type { IData } from "../../../../types";
import { formatNumber } from "../../../../utils/NumbersFormatting";
import { Filters } from "../Filters";
import { CheckboxColumn, ItemColumn } from "./columns";

interface IProps {
  items: IData[];
  totalItems?: number;
  isLoading?: boolean;
  applications?: IApplication[];
  goPrevPage?: () => void;
  onSort: (orderBy: string, direction: Direction) => void;
}

const CacheTable: React.FC<IProps> = ({
  items,
  totalItems = 0,
  isLoading = false,
  applications,
  goPrevPage,
  onSort,
}) => {
  const [tableData, setTableData] = useState<IData[]>([]);
  const [rowToDelete, setRowToDelete] = useState<{
    id: string;
    projId: string;
  } | null>(null);
  const navigate = useNavigate();
  const [confirmModalVisible, setConfirmModalVisible] = useState("");
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const { isMd } = useMediaQueryContext();
  const { activeSorting, setActiveSorting } = useSortableTable();

  const location = useLocation();
  const { currentOrganization } = useOrganizationContext();
  const { setPrevLocation } = useLocationContext();

  const deleteMutation = useDeleteCacheItem();
  const deleteBulkMutation = useDeleteCacheItems();

  const handleCancelDelete = () => {
    setConfirmModalVisible("");
  };

  const handleDelete = () => {
    if (confirmModalVisible === "single") {
      deleteMutation.mutate({
        cacheItemId: rowToDelete?.id || "",
        organizationId: currentOrganization?.id || "",
        applicationId: rowToDelete?.projId || "",
      });
    }

    if (confirmModalVisible === "bulk") {
      const itemsToDelete: {
        applicationId: string;
        cacheItemId: string;
      }[] = selectedItems
        .filter(id => {
          const item = items.find(i => i.id === id);
          return !!item;
        })
        .map(id => {
          const item = items.find(i => i.id === id);
          return {
            applicationId: item?.applicationId || "",
            cacheItemId: item?.id || "",
          };
        });
      deleteBulkMutation.mutate({
        cacheItemsIds: itemsToDelete,
        organizationId: currentOrganization?.id || "",
      });
    }
  };

  const handleSelectAll = useCallback(
    (e: React.FormEvent<HTMLInputElement>) => {
      const { checked } = e.currentTarget;
      const ids = tableData
        .filter(item => item.state === "Ready")
        .map(item => item.id);
      setSelectedItems(checked ? ids : []);
    },
    [tableData],
  );

  const handleSelectRow = useCallback(
    (e: React.FormEvent<HTMLInputElement>) => {
      const { checked, value } = e.currentTarget;
      if (checked) {
        setSelectedItems([...selectedItems, value]);
      } else {
        setSelectedItems(selectedItems.filter(id => id !== value));
      }
    },
    [selectedItems, setSelectedItems],
  );

  useEffect(() => {
    setTableData(items);
  }, [items]);

  useEffect(() => {
    if (deleteMutation.isSuccess) {
      if (items.length === 1) {
        goPrevPage && goPrevPage();
      }
      setConfirmModalVisible("");
      deleteMutation.reset();
    }
  }, [deleteMutation, goPrevPage, items.length]);

  useEffect(() => {
    if (deleteBulkMutation.isSuccess) {
      if (items.length === selectedItems.length) {
        goPrevPage && goPrevPage();
      }
      setConfirmModalVisible("");
      setSelectedItems([]);
      deleteBulkMutation.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deleteBulkMutation, goPrevPage]);

  const goToEdit = useCallback(() => {
    setPrevLocation(`/safety-compliance/cache/${location.search}`);
  }, [location.search, setPrevLocation]);

  const renderActions = useCallback(
    (props: CellProps<IData>) => (
      <ActionCell
        disableDeleteBtn={props.row?.original?.state !== "Ready"}
        onDeleteClick={() => {
          if (props.row?.original?.state !== "Ready") return;
          setRowToDelete({
            id: props.row?.original?.id,
            projId: props.row?.original?.applicationId,
          });
          setConfirmModalVisible("single");
          deleteMutation.reset();
          deleteBulkMutation.reset();
        }}
      />
    ),
    [
      setRowToDelete,
      deleteMutation,
      deleteBulkMutation,
      setConfirmModalVisible,
    ],
  );

  const checkboxHeader = useCallback(() => {
    return (
      <Checkbox
        value="all"
        checked={
          !!selectedItems.length &&
          selectedItems.length ===
            tableData.filter(item => item.state === "Ready").length
        }
        onChange={handleSelectAll}
      />
    );
  }, [selectedItems, tableData, handleSelectAll]);

  const checkBoxCell = useCallback(
    (props: CellProps<IData>) => {
      return (
        <CheckboxColumn
          cellProps={props}
          selectedItems={selectedItems}
          onChange={handleSelectRow}
        />
      );
    },
    [selectedItems, handleSelectRow],
  );

  const handleSort = useCallback(
    (orderBy: string, direction: Direction) => {
      onSort(orderBy, direction);
      setActiveSorting(orderBy, direction);
    },
    [onSort, setActiveSorting],
  );

  const hitsHeader = useCallback(() => {
    return (
      <SortableHeader
        title="hits"
        onSort={handleSort}
        orderBy="metadata.hits"
        activeSorting={activeSorting}
      />
    );
  }, [handleSort, activeSorting]);

  const dateHeader = useCallback(() => {
    return (
      <SortableHeader
        title="date"
        onSort={handleSort}
        orderBy="created"
        activeSorting={activeSorting}
      />
    );
  }, [handleSort, activeSorting]);

  const itemCell = useCallback(
    (props: CellProps<IData>) => {
      return <ItemColumn cellProps={props} applications={applications || []} />;
    },
    [applications],
  );

  const renderDate = useCallback((props: CellProps<IData>) => {
    return <DateColumn date={props.row?.original?.created} type="short" />;
  }, []);

  const renderHit = useCallback((props: CellProps<IData>) => {
    return <>{formatNumber(props.row.original.metadata?.hits)}</>;
  }, []);

  const renderSM = useCallback(
    (props: CellProps<IData>) => {
      return (
        <TableLineSm
          title={
            <div className="d-flex w-auto align-items-center">
              <ItemColumn cellProps={props} applications={applications || []} />
            </div>
          }
          controls={renderActions(props)}
        >
          {/* <TableCellSm title={<RiskColumnHeader />}><RiskColumn {...props}/></TableCellSm> */}
          <TableCellSm title="hits">{renderHit(props)}</TableCellSm>
          <TableCellSm title="Date">{renderDate(props)}</TableCellSm>
        </TableLineSm>
      );
    },
    [applications, renderActions, renderDate, renderHit],
  );

  const columns: Column<IData>[] = [
    {
      Header: checkboxHeader,
      accessor: "checkbox",
      minWidth: 16,
      width: 16,
      Cell: checkBoxCell,
    },
    {
      Header: "Item",
      accessor: "prompt",
      minWidth: 150,
      width: "70%",
      Cell: itemCell,
    },
    {
      Header: hitsHeader,
      accessor: "hits",
      minWidth: 75,
      maxWidth: 100,
      Cell: renderHit,
    },
    {
      Header: dateHeader,
      accessor: "created",
      Cell: renderDate,
    },
    {
      Header: "",
      id: "edit",
      minWidth: 50,
      width: 50,
      maxWidth: 50,
      Cell: renderActions,
    },
  ];

  const columnsSm: Column<IData>[] = [
    {
      Header: "Cache items",
      accessor: "prompt",
      Cell: renderSM,
    },
  ];

  const resetError = () => {
    deleteMutation.reset();
    deleteBulkMutation.reset();
  };
  const handleRowClick = (row: IData) => {
    navigate(`/safety-compliance/cache/${row.applicationId}/${row.id}`);
    goToEdit();
  };

  return (
    <>
      {!!confirmModalVisible && (
        <DeleteCacheItemPopup
          onCancel={handleCancelDelete}
          onSubmit={handleDelete}
          isProcessing={
            deleteMutation.isLoading || deleteBulkMutation.isLoading
          }
          isError={deleteMutation.isError || deleteBulkMutation.isError}
          itemsCount={confirmModalVisible === "bulk" ? selectedItems.length : 1}
        />
      )}
      <Filters
        applications={applications || []}
        selectedItems={selectedItems.length}
        handleDeleteClick={() => {
          resetError();
          setConfirmModalVisible("bulk");
        }}
      />

      {isMd && <Sorting activeSorting={activeSorting} onSort={handleSort} />}

      <PaginatedTable
        columns={isMd ? columnsSm : columns}
        data={tableData}
        totalItems={totalItems}
        isLoading={isLoading}
        onRowClick={handleRowClick}
      />
    </>
  );
};

export default CacheTable;
